import { Box, Skeleton, Typography } from "@mui/material";
import { Grid2 as Grid, Paper, styled } from "@mui/material";
import { OrganizationProfileInfo } from "../../../../../types";
import { EditIcon } from "../../../../icons";
import { theme } from "../../../../../utils";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { ImageCropModal } from "../../../../common";
import { toastOptionsAtom } from "../../../../../atoms";
import { COUNTRIES } from "../../../../../constants";
import { useAtom } from "jotai";

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  flexGrow: 1,
  borderRadius: "10px",
  boxShadow: "none",
}));

type ViewOrganizationProfileHeaderProps = {
  data?: OrganizationProfileInfo;
  isLoading?: boolean;
  handleEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  handleUploadProfilePicture: ({
    data,
    logo,
  }: {
    data: Pick<OrganizationProfileInfo, "name" | "logo" | "country">;
    logo?: File;
  }) => void;
};

export const ViewOrganizationProfileHeader = ({
  data,
  isLoading,
  handleEditMode,
  handleUploadProfilePicture,
}: ViewOrganizationProfileHeaderProps) => {
  const [uploadedImage, setUploadedImage] = useState<File>();
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>();
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (uploadedImage) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        setUploadedImageUrl(e.target.result as string);
      };
      reader.readAsDataURL(uploadedImage);
    }
  }, [uploadedImage]);

  const handleConfirm = (file: File) => {
    setUploadedImage(file);
    setCropModalOpen(false);

    if (data && uploadedImage) {
      const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const fileExtension = uploadedImage.name.split(".").pop();
      const fileName = `logo_${timestamp}.${fileExtension}`;

      handleUploadProfilePicture({
        data: { name: data.name, country: data.country, logo: fileName },
        logo: file,
      });
    }
  };

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;

    if (file && file.type.startsWith("image/")) {
      setUploadedImage(file);
      setCropModalOpen(true);
    } else {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }

    event.target.value = "";
  };

  return (
    <>
      <Grid
        container
        spacing={{ xs: 0, sm: 2 }}
        sx={{
          width: "100%",
          minHeight: "100%",
          position: "relative",
          px: 3,
          pt: 3,
        }}
      >
        <Grid
          size={{ xs: 12 }}
          sx={{
            px: { xs: 0, sm: 4 },
          }}
        >
          <Item
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" },
              gap: { xs: 1.5, sm: 1 },
              pl: 0,
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              position={"relative"}
              sx={{
                borderRadius: "50%",
                overflow: "hidden",
                width: { xs: 70, sm: 80 },
                height: { xs: 70, sm: 80 },
              }}
            >
              {isLoading ? (
                <Skeleton
                  variant="circular"
                  sx={{
                    width: { xs: 70, sm: 80 },
                    height: { xs: 70, sm: 80 },
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={uploadedImageUrl ? uploadedImageUrl : data?.logo}
                  alt="Organization logo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              )}

              <input
                ref={uploadInputRef}
                type="file"
                name="logo"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
            </Box>

            <Box
              sx={{
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => uploadInputRef.current?.click()}
              >
                Profile image / Logo
              </Typography>

              <Typography sx={{ color: "#a6a6a6" }}>
                (Image must be in jpeg/png format)
              </Typography>
            </Box>
          </Item>
        </Grid>

        <Box
          onClick={() => handleEditMode(true)}
          sx={{
            position: "absolute",
            right: "1rem",
            top: "1rem",
            cursor: "pointer",
          }}
        >
          {data && <EditIcon width={"21px"} active={true} />}
        </Box>

        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{
            px: { xs: 0, sm: 4 },
            pt: 3,
            pb: { xs: 0, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: "#606060",
                fontSize: "14px",
              }}
            >
              Organization Name
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {data?.name || "-"}
            </Typography>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{
            px: { xs: 0, sm: 4 },
            pt: 3,
            pb: { xs: 0, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: "#606060",
                fontSize: "14px",
              }}
            >
              Country
            </Typography>
            <Typography sx={{ fontWeight: 500 }}>
              {COUNTRIES.find((c) => c.value === data?.country)?.key || "-"}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      {uploadedImage && (
        <ImageCropModal
          open={cropModalOpen}
          file={uploadedImage}
          onClose={() => setCropModalOpen(false)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
